import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { ipxUrl } from '@/utils/ipix';

/**
 * ImgWithFallback
 *
 * Menyediakan:
 * - Manual srcSet via `srcSet`
 * - Manual WebP srcSet via `srcSetWebp`
 * - Legacy dua URL via `srcAvif`/`srcWebp`
 * - Dynamic IPX mode via `src`, `width`, `height`, `quality`, `ipxBase`
 * - Fallback original via `fallbackSrc`
 * - Responsive sizes via `sizes`
 */
const ImgWithFallback = ({
  srcAvif,
  srcWebp,
  src,
  width,
  height,
  sharpen,
  quality = 90,
  ipxBase,
  srcSet,
  srcSetWebp,
  fallbackSrc,
  sizes,
  alt,
  className = '',
  onError,
  style,
  loading = 'lazy',
  ...delegated
}) => {
  const [hasError, setHasError] = useState(false);

  const handleError = (e) => {
    if (!hasError) {
      setHasError(true);
      if (onError) onError(e);
    }
  };

  let ipxAvif = null;
  let ipxWebp = null;

  if (src && width && height && !hasError) {
    const ipxOpts = { width, height, quality, sharpen };
    ipxAvif = ipxUrl(src, { ...ipxOpts, format: 'avif' }, ipxBase);
    ipxWebp = ipxUrl(src, { ...ipxOpts, format: 'webp' }, ipxBase);
  }

  const avifSourceForTag = !hasError ? srcAvif || ipxAvif : null;
  const webpBaseUrl = !hasError ? srcWebp || ipxWebp : null;
  const webpSourceForTag = !hasError ? srcSetWebp || webpBaseUrl : null;
  // Prioritize IPX optimized images, fallback to original
  const imgSrcToUse = hasError 
    ? fallbackSrc || src || '' 
    : ipxWebp || srcWebp || src || fallbackSrc || '';

  return (
    <picture className={className} {...delegated}>
      {/* AVIF source */}
      {avifSourceForTag && <source type="image/avif" srcSet={avifSourceForTag} sizes={sizes} />}
      {/* WebP source */}
      {webpSourceForTag && <source type="image/webp" srcSet={webpSourceForTag} sizes={sizes} />}
      {/* Fallback <img> */}
      <img
        src={imgSrcToUse}
        {...(srcSet && !hasError ? { srcSet } : {})}
        sizes={sizes}
        alt={alt}
        className={className}
        loading={loading}
        onError={handleError}
        style={{
          maxWidth: width ? `${width}px` : 'none',
          maxHeight: height ? `${height}px` : 'none',
          ...style,
        }}
        {...delegated}
      />
    </picture>
  );
};

ImgWithFallback.propTypes = {
  // Legacy
  srcAvif: PropTypes.string,
  srcWebp: PropTypes.string,
  // IPX mode
  src: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  sharpen: PropTypes.number,
  quality: PropTypes.number,
  ipxBase: PropTypes.string,
  // Manual overrides
  srcSet: PropTypes.string,
  srcSetWebp: PropTypes.string,
  fallbackSrc: PropTypes.string,
  sizes: PropTypes.string,
  // Common
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  loading: PropTypes.oneOf(['lazy', 'eager']),
  onError: PropTypes.func,
  style: PropTypes.object,
};

export default ImgWithFallback;
